import { DIRECTIVES_ID } from '@constants/survey-ids';

import { ConsentRecord } from '@models/consent';

import { consentToAllMap, consentToNoneMap } from '@utils/legalDirective';
import { getLegalDirMap } from '@utils/legalDirective';

import type { ConsentModel } from 'generated/types';

export type CategoryType =
  'blood-products' | 'heroic-measures' | 'specific-organs';

export class LegalDirectiveRecord extends ConsentRecord {
  public model: ConsentModel;

  public constructor(model: ConsentModel) {
    super(model);
    this.model = model;
  }

  public get selections() {
    return (this.model.extension ?? [])
      .filter(({ url }) =>
        Object.keys(consentToAllMap).some((consent) =>
          url.includes(`${DIRECTIVES_ID}/${consent}`)
        )
      )
      .map(({ url, valueBoolean }) => ({
        url,
        valueBoolean,
      }));
  }

  public get lastUpdate() {
    return this.model.meta?.lastUpdated;
  }

  public consentToAll(type: CategoryType) {
    const directive = this.findExtension(consentToAllMap[type]);
    return directive?.valueBoolean ?? false;
  }

  public consentToNone(type: CategoryType) {
    const directive = this.findExtension(consentToNoneMap[type]);
    return directive?.valueBoolean ?? false;
  }

  public getDirectiveText(type: CategoryType) {
    const selected = (this.model.extension ?? []).map(({ url }) =>
      url.substring(url.lastIndexOf('/') + 1)
    );

    const typeMap = getLegalDirMap(type);

    if (selected.includes(consentToAllMap[type])) {
      return [typeMap[consentToAllMap[type]]];
    } else if (selected.includes(consentToNoneMap[type])) {
      return [typeMap[consentToNoneMap[type]]];
    }
    return selected.map((sel) => typeMap[sel]);
  }

  public isHeroicMeasures() {
    const result = this.model.category.some((cat) =>
      cat.coding?.some((c) => c.code === 'heroic-measures')
    );
    return result;
  }

  public isOrganDonation() {
    const result = this.model.category.some((cat) =>
      cat.coding?.some((c) => c.code === 'specific-organs')
    );
    return result;
  }

  public isBloodProducts() {
    const result = this.model.category.some((cat) =>
      cat.coding?.some((c) => c.code === 'blood-products')
    );
    return result;
  }
}
